import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import i18nConfig from 'utils/i18nConfig'
import Layout from 'components/layout/Layout'
import BlogArticle from 'features/blog/article/BlogArticle'
import { shuffleArray } from 'utils/helpers'
import { DEFAULT_NAMESPACES } from 'constants/i18n'
import type { SeoProps } from 'components/seo/types'
import { LocalizationModel } from 'types/cms/components'
import { Localizations } from 'types/language'
import { routes } from 'utils/routes'
import { cmsLib } from 'lib'
import { BlogArticleProps } from 'features/blog/article/types'
import { getLowerCaseLanguage } from 'utils/language'

const BlogArticlePage = (props: BlogArticleProps) => {
  // Constants
  const { seo, title } = props.article
  const SEO: SeoProps = {
    description: seo?.metaDescription,
    image: seo?.seoImage?.url,
    title: seo?.seoTitle || title
  }

  return (
    <Layout seo={SEO} localized={props.localizations}>
      <BlogArticle {...props} />
    </Layout>
  )
}

export const getStaticProps = async ({ params, locale }) => {
  const language = getLowerCaseLanguage(locale)

  // Get the slug from the params
  const { article: slug } = params

  // Get the article and all articles
  const [article, allArticles] = await Promise.all([cmsLib.getBlogArticleBySlug(slug, language), cmsLib.getBlogArticles(language)])

  // If the article is not found, return a 404
  if (!article) {
    return {
      notFound: true,
      revalidate: 5
    }
  }

  // Shuffle, slice & filter articles
  const shuffledArticles = shuffleArray(allArticles)
  const articles = shuffledArticles.slice(0, 5).filter((article) => article?.slug !== slug)

  // Create the localizations
  const localizations: Localizations = article.localizations?.reduce<Localizations>(
    (prev: Localizations, curr: LocalizationModel) => {
      if (!curr.publishedAt) return prev

      return {
        ...prev,
        [curr.locale]: routes(curr.locale, curr.slug).blogArticle
      }
    },
    { [language]: routes(language, slug).blogArticle }
  )

  return {
    props: {
      article,
      localizations,
      otherArticles: articles,
      ...(await serverSideTranslations(locale, [...DEFAULT_NAMESPACES, 'blog', 'encyclopedia'], i18nConfig))
    }
  }
}

export const getStaticPaths = async () => {
  const articles = await cmsLib.getBlogArticles('all')
  const paths = articles.map((article) => [{ locale: article.locale, params: { article: article.slug } }]).flat()

  return {
    fallback: 'blocking',
    paths // will server-render pages on-demand if the path does not exist
  }
}

export default BlogArticlePage
